import { useTranslation } from '@portals-platform/i18n';
import type { StatusTone } from '@portals-platform/ui';
import {
  CheckCircle2Icon,
  CircleAlertIcon,
  ClockIcon,
  HourglassIcon,
  type LucideIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import { describeCertStatus } from '../../lib/cert-status.js';

const TONE_TOKEN: Record<StatusTone, string> = {
  neutral: 'var(--color-muted-foreground)',
  info: 'var(--color-info)',
  success: 'var(--color-success)',
  warning: 'var(--color-warning)',
  danger: 'var(--color-destructive)',
};

const TONE_ICON: Record<StatusTone, LucideIcon> = {
  neutral: ClockIcon,
  info: HourglassIcon,
  success: CheckCircle2Icon,
  warning: TriangleAlertIcon,
  danger: CircleAlertIcon,
};

interface CertificateStatusBannerProps {
  status: string;
  issuedOn: string;
  issuedBy: string;
}

export function CertificateStatusBanner({
  status,
  issuedOn,
  issuedBy,
}: CertificateStatusBannerProps) {
  const { t } = useTranslation();
  const { label, tone } = describeCertStatus(status);
  const token = TONE_TOKEN[tone];
  const Icon = TONE_ICON[tone];

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-[var(--radius-lg)] border px-4 py-3 text-[13px]"
      style={{
        // Mixed against the surface and ink tokens so the banner reads in both themes.
        background: `color-mix(in oklab, ${token} 10%, var(--color-surface))`,
        borderColor: `color-mix(in oklab, ${token} 40%, var(--color-border))`,
        color: `color-mix(in oklab, ${token} 72%, var(--color-foreground))`,
      }}
    >
      <div className="flex items-center gap-2 font-semibold">
        <Icon className="size-4" style={{ color: token }} aria-hidden="true" />
        {label}
      </div>
      <p className="opacity-85">
        {issuedBy
          ? t('certificates.details.issuedBy', { date: issuedOn, user: issuedBy })
          : t('certificates.details.issued', { date: issuedOn })}
      </p>
    </div>
  );
}
